import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { GpuServer } from '../types/models/GpuServer';
import type { ApiResponseGpuServer } from '../types/api/ApiResponseGpuServer';
import { jsendErrorMessage, readJsendBody } from './gpuServerResponse';

export type GpuServerDeleteOutcome =
  /** Normally `deleting`, sometimes already `deleted`, with `ip: null`. */
  | { status: 'success'; server: GpuServer }
  /** A refusal shown verbatim (impersonation, still starting, failed/unavailable, not found). */
  | { status: 'error'; message: string };

export interface UseDeleteGpuServerReturn {
  /** Never throws. */
  remove: (id: number) => Promise<GpuServerDeleteOutcome>;
  loading: boolean;
}

/**
 * `DELETE /datacenters/gpu-servers/{id}` — delete a GPU server and its disk
 * (docs/api-types/gpu-servers.md). The current, already-paid hour is not refunded. Idempotent: a
 * `deleting` server is re-attempted and a `deleted` one returned unchanged, both as a success.
 */
export const useDeleteGpuServer = (): UseDeleteGpuServerReturn => {
  const apiFetch = useApiFetch();
  const [loading, setLoading] = useState(false);

  const remove = useCallback(
    async (id: number): Promise<GpuServerDeleteOutcome> => {
      setLoading(true);
      try {
        const response = await apiFetch(`/datacenters/gpu-servers/${id}`, { method: 'DELETE' });
        const body = await readJsendBody<ApiResponseGpuServer>(response);
        if (body?.status === 'success' && body.data?.server) {
          return { status: 'success', server: body.data.server };
        }
        return { status: 'error', message: jsendErrorMessage(body, 'Could not delete this GPU server.') };
      } catch (err) {
        return {
          status: 'error',
          message: err instanceof Error ? err.message : 'An error occurred while deleting this GPU server.',
        };
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  return { remove, loading };
};

export default useDeleteGpuServer;
